// Shared pin board for My Desk Canvas (tables), People, and Quick Docs.
//
// N-090c (owner, 2026-09-08 — "every panel shows its name twice", "the
// panel's own action controls... right-aligned in the same bar"): the
// Add/Remove selects used to live in this component's OWN `.section-head`,
// stacked visually below DeskGrid.tsx's title bar — a second header UNDER
// the first. `chromeless` drops that inner header entirely (same contract as
// DeskPanel.tsx's own prop of the same name) so a panel rendered on the
// canvas gets exactly one heading, DeskGrid's; `PinnedBoardActions` below is
// the same Add/Remove markup, split out so panelCatalog.tsx can hand it to
// DeskGrid to render INSIDE that one title bar, right-aligned, instead of
// losing the controls when the header they used to live in disappears.
// Outside the canvas (chromeless=false, the default — this file's own
// dedicated tests render `<Canvas />`/`<QuickDocs />` standalone), nothing
// changes: the header and its actions render exactly as before.
import type { JSX } from 'preact';
import { navigate } from '../lib/router';
import { useTableSort } from '../lib/tableSort';
import { SortableTh } from './SortableTh';
import { EmptyState, FaultState } from './FaultState';
import { FavoriteStar } from './FavoriteStar';
import {
  starItem, unstarItem, useFavorites,
  type FavoriteCatalogItem, type FavoriteItem, type FavoritesData,
} from '../lib/favorites';

function openHref(href: string): void {
  if (/^https?:\/\//.test(href)) {
    window.open(href, '_blank', 'noopener,noreferrer');
    return;
  }
  const [path, hash] = href.split('#');
  navigate(path || '/');
  if (hash) {
    window.location.hash = hash;
    requestAnimationFrame(() => {
      document.getElementById(hash)?.scrollIntoView({ block: 'start' });
    });
  }
}

/** The rows and the not-yet-pinned catalog items ONE board (Canvas/People/
 * Quick Docs) cares about, out of the whole shared favourites answer — a
 * pure filter so `PinnedBoard` and `PinnedBoardActions` (two separate
 * mounts, each with its own `useFavorites()` subscription — see this file's
 * module comment) never risk computing this two different ways. */
function boardRows(
  data: FavoritesData, kind: FavoriteItem['kind'],
): { rows: FavoritesData['favorites']; addable: FavoriteCatalogItem[] } {
  const all = Array.isArray(data.favorites) ? data.favorites : [];
  const catalog = Array.isArray(data.catalog) ? data.catalog : [];
  const rows = all.filter((row) => row.kind === kind);
  const addable = catalog.filter(
    (item) => item.kind === kind && !all.some((row) => row.item_key === item.item_key),
  );
  return { rows, addable };
}

async function addFromList(item: FavoriteCatalogItem): Promise<void> {
  await starItem(item.item_key);
}

async function removeFromList(id: number): Promise<void> {
  await unstarItem(id);
}

/** The Add/Remove controls alone, for DeskGrid's title bar
 * (`entry.renderActions` in panelCatalog.tsx) — null once there is nothing
 * to add or remove, exactly like the inline version below used to disappear
 * on its own. Stops its own `pointerdown` from bubbling: unlike the inline
 * version, this one lives inside DeskGrid's draggable title bar, and without
 * this a click to open the select would start a panel move instead. */
export function PinnedBoardActions(
  {
    kind, title, testId, allowAdd = false, allowRemove = false,
  }: {
    kind: FavoriteItem['kind'];
    title: string;
    testId: string;
    allowAdd?: boolean;
    allowRemove?: boolean;
  },
): JSX.Element | null {
  const data = useFavorites();
  if (data.status !== 'ready') return null;
  const { rows, addable } = boardRows(data.data, kind);
  if (!(allowAdd && addable.length > 0) && !(allowRemove && rows.length > 0)) return null;

  return (
    <div class="section-head-actions desk-panel-titlebar-actions" onPointerDown={(ev) => ev.stopPropagation()}>
      {allowAdd && addable.length > 0 ? (
        <select
          class="desk-canvas-add"
          aria-label={`Add to ${title}`}
          data-testid={`${testId}-add`}
          value=""
          onChange={(ev: Event) => {
            const key = (ev.target as HTMLSelectElement).value;
            const item = addable.find((a) => a.item_key === key);
            if (item) void addFromList(item);
          }}
        >
          <option value="">Add a table…</option>
          {addable.map((item) => (
            <option key={item.item_key} value={item.item_key}>{item.label}</option>
          ))}
        </select>
      ) : null}
      {allowRemove && rows.length > 0 ? (
        <select
          class="desk-canvas-add"
          aria-label={`Remove from ${title}`}
          data-testid={`${testId}-remove`}
          value=""
          onChange={(ev: Event) => {
            const id = Number((ev.target as HTMLSelectElement).value);
            if (id) void removeFromList(id);
          }}
        >
          <option value="">Remove a table…</option>
          {rows.map((row) => (
            <option key={row.id} value={String(row.id)}>{row.label}</option>
          ))}
        </select>
      ) : null}
    </div>
  );
}

export function PinnedBoard(
  {
    kind, title, empty, testId, className, allowAdd = false, allowRemove = false, chromeless = false,
  }: {
    kind: FavoriteItem['kind'];
    title: string;
    empty: string;
    testId: string;
    className: string;
    allowAdd?: boolean;
    allowRemove?: boolean;
    chromeless?: boolean;
  },
): JSX.Element {
  const data = useFavorites();

  if (data.status === 'loading') {
    return (
      <div class={`card ${className}`} data-testid={testId}>
        {chromeless ? null : <div class="section-head"><h2>{title}</h2></div>}
        <EmptyState label={`Loading ${title}…`} />
      </div>
    );
  }
  if (data.status === 'fault') {
    return (
      <div class={`card ${className}`} data-testid={testId}>
        {chromeless ? null : <div class="section-head"><h2>{title}</h2></div>}
        <FaultState name={`${testId}_load_failed`} detail={data.error} />
      </div>
    );
  }

  const { rows, addable } = boardRows(data.data, kind);
  const sorted = useTableSort(rows, { name: (row) => row.label });

  return (
    <div class={`card ${className}`} data-testid={testId}>
      {chromeless ? null : (
        <div class="section-head">
          <div class="section-head-row">
            <h2>{title}</h2>
            {(allowAdd && addable.length > 0) || (allowRemove && rows.length > 0) ? (
              <div class="section-head-actions">
                {allowAdd && addable.length > 0 ? (
                  <select
                    class="desk-canvas-add"
                    aria-label={`Add to ${title}`}
                    data-testid={`${testId}-add`}
                    value=""
                    onChange={(ev: Event) => {
                      const key = (ev.target as HTMLSelectElement).value;
                      const item = addable.find((a) => a.item_key === key);
                      if (item) void addFromList(item);
                    }}
                  >
                    <option value="">Add a table…</option>
                    {addable.map((item) => (
                      <option key={item.item_key} value={item.item_key}>{item.label}</option>
                    ))}
                  </select>
                ) : null}
                {allowRemove && rows.length > 0 ? (
                  <select
                    class="desk-canvas-add"
                    aria-label={`Remove from ${title}`}
                    data-testid={`${testId}-remove`}
                    value=""
                    onChange={(ev: Event) => {
                      const id = Number((ev.target as HTMLSelectElement).value);
                      if (id) void removeFromList(id);
                    }}
                  >
                    <option value="">Remove a table…</option>
                    {rows.map((row) => (
                      <option key={row.id} value={String(row.id)}>{row.label}</option>
                    ))}
                  </select>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      )}
      {rows.length === 0 ? (
        <EmptyState label={empty} />
      ) : (
        <div class="table-wrap desk-panel-scroll">
          <table>
            <thead>
              <tr>
                <SortableTh label="Name" sortKey="name" state={sorted.state} onSort={sorted.toggle} />
                <th />
              </tr>
            </thead>
            <tbody>
              {sorted.rows.map((row) => (
                <tr key={row.id} data-testid={`${testId}-row-${row.item_key}`}>
                  <td>
                    <FavoriteStar itemKey={row.item_key} />
                    {' '}
                    {row.label}
                  </td>
                  <td>
                    <button
                      type="button"
                      class="link"
                      data-testid={`${testId}-open-${row.item_key}`}
                      onClick={() => openHref(row.href)}
                    >
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
